import React, { useContext, useState, useEffect, useRef } from "react";
import { Table, Input, Button, Popconfirm, Form, InputNumber } from "antd";
import "./Financials.scss";
import _ from "lodash";
import Localize from "../../libs/localization";

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} is required.`,
          },
        ]}
      >
        <InputNumber
          style={{ width: "100%" }}
          ref={inputRef}
          onPressEnter={save}
          onBlur={save}
        />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

class Financials extends React.Component {
  constructor(props) {
    super(props);
    this.renderColumns(this.props.editable);
    this.state = this.renderData(this.props.dataSource);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.editable !== this.props.editable) {
      this.renderColumns(this.props.editable);
    }
    if (prevProps.dataSource !== this.props.dataSource) {
      this.setState(this.renderData(this.props.dataSource));
    }
  }

  renderData = (dataSource) => {
    if (dataSource) {
      const data = [];
      Object.keys(dataSource).map((year, i) => {
        const tempData = {};
        tempData.year = year;
        tempData.key = i;
        tempData.revenue = dataSource[year].revenue;
        tempData.operatingResult = dataSource[year].operatingResult;
        tempData.employees = dataSource[year].employees;
        tempData.personnelCost = dataSource[year].personnelCost;
        data.push(tempData);
      });
      return {
        dataSource: data,
        count: data.length,
      };
    } else {
      return {
        dataSource: [],
        count: 0,
      };
    }
  };
  renderColumns = (editable) => {
    this.columns = [
      {
        title: Localize("COMMON.YEAR"),
        dataIndex: "year",
        width: "30%",
        editable,
      },
      {
        title: Localize("COMMON.REVENUE"),
        dataIndex: "revenue",
        editable,
      },
      {
        title: Localize("COMMON.INCOME"),
        dataIndex: "operatingResult",
        editable,
      },
      {
        title: Localize("COMMON.EMPLOYEES"),
        dataIndex: "employees",
        editable,
      },
      {
        title: Localize("COMMON.PERSONNEL_COST"),
        dataIndex: "personnelCost",
        editable,
      },
    ];
    editable &&
      this.columns.push({
        title: "operation",
        dataIndex: "operation",
        render: (_, record) =>
          this.state.dataSource.length >= 1 ? (
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => this.handleDelete(record.key)}
            >
              <a>Delete</a>
            </Popconfirm>
          ) : null,
      });
  };
  handleDelete = (key) => {
    const dataSource = [...this.state.dataSource];
    this.setState({
      dataSource: dataSource.filter((item) => item.key !== key),
    });
    this.props.onChange(
      this.manageDataToSend(dataSource.filter((item) => item.key !== key))
    );
  };
  handleAdd = () => {
    const { count, dataSource } = this.state;
    const newData = {
      key: count,
      year: "",
      revenue: "",
      operatingResult: "",
      employees: "",
      personnelCost: "",
    };
    this.setState({
      dataSource: [...dataSource, newData],
      count: count + 1,
    });
  };
  handleSave = (row) => {
    const newData = [...this.state.dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, { ...item, ...row });
    this.setState({
      dataSource: newData,
    });
    this.props.onChange(this.manageDataToSend(this.state.dataSource));
  };
  manageDataToSend = (dataSource) => {
    const objToSend = {};
    dataSource.map((singleData) => {
      const yearObj = _.flow([
        Object.entries,
        (arr) => arr.filter(([key, value]) => key !== "year" && key !== "key"),
        Object.fromEntries,
      ])(singleData);
      objToSend[singleData.year] = yearObj;
    });
    return objToSend;
  };

  render() {
    const { dataSource } = this.state;
    const { editable } = this.props;
    const components = {
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    };
    const columns = this.columns.map((col) => {
      if (!col.editable) {
        return col;
      }

      return {
        ...col,
        onCell: (record) => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave: this.handleSave,
        }),
      };
    });
    return (
      <div>
        {editable && (
          <Button
            onClick={this.handleAdd}
            type="primary"
            style={{
              marginBottom: 16,
            }}
          >
            Add a row
          </Button>
        )}
        <Table
          components={components}
          rowClassName={() => "editable-row"}
          bordered
          dataSource={dataSource}
          columns={columns}
        />
      </div>
    );
  }
}

export default Financials;
